"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import type { MotionValue, Variants } from "motion/react";
import { cn } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

type Step = {
  image: string;
  title: string;
  body: React.ReactNode;
  alt: string;
};

type Panel = {
  id: string;
  label: string;
  title: string;
  steps: Step[];
};

// ── Steps (content + imagery from the Figma app flows) ───────────────────────

const FLOW_IMAGE_VERSION = "20260731";

function flowImage(path: string) {
  return path.replace(".png", `-${FLOW_IMAGE_VERSION}.png`);
}

const BUY_STEPS: Step[] = [
  {
    image: flowImage("/buy-step-1.png"),
    title: "Choose your amount",
    alt: "JustGold app screen to buy 24K digital gold, real metal backed",
    body: (
      <>
        Enter dirhams or grams,{" "}
        <span className="font-semibold text-accent-500">from AED 10 up.</span>{" "}
        You&apos;ll see exactly how much gold that buys at today&apos;s rate.
      </>
    ),
  },
  {
    image: flowImage("/buy-step-2.png"),
    title: "Pay securely",
    alt: "JustGold app payment screen with a linked bank and order breakdown",
    body: (
      <>Card or bank transfer, with the full breakdown shown before you confirm.</>
    ),
  },
  {
    image: flowImage("/buy-step-3.png"),
    title: "It's in your vault",
    alt: "Confirmation that the gold is insured and stored in a secure vault",
    body: (
      <>Your gold appears in the app immediately, stored and insured in Dubai.</>
    ),
  },
];

const SELL_STEPS: Step[] = [
  {
    image: flowImage("/sell-step-1.png"),
    title: "Choose how much",
    alt: "JustGold app screen to sell 24K digital gold in AED or grams",
    body: (
      <>
        Sell any amount, in dirhams or grams, at the live sell price.{" "}
        <span className="font-semibold text-accent-500">
          No notice, no penalty.
        </span>
      </>
    ),
  },
  {
    image: flowImage("/sell-step-2.png"),
    title: "Pick your account",
    alt: "JustGold app screen to select a linked UAE bank account for payout",
    body: <>Send it to any linked UAE bank account.</>,
  },
  {
    image: flowImage("/sell-step-3.png"),
    title: "Money in your bank",
    alt: "Confirmation screen that the gold has been sold and payout scheduled",
    body: <>Funds arrive within 2 to 3 working days.</>,
  },
];

const DELIVERY_STEPS: Step[] = [
  {
    image: flowImage("/delivery-step-1.png"),
    title: "Choose your coin or bar",
    alt: "JustGold app catalog to pick a coin or bar",
    body: <>Minimum value, from 1g upwards.</>,
  },
  {
    image: flowImage("/delivery-step-2.png"),
    title: "Review and confirm",
    alt: "JustGold app order review with price breakdown and delivery estimate",
    body: (
      <>Use the gold already in your vault and see every charge before you pay.</>
    ),
  },
  {
    image: "/delivery-step-3-20260812.png",
    title: "Track it to your door",
    alt: "JustGold app order tracking with a courier holding a Just Gold box",
    body: <>Insured door to door, anywhere in the UAE.</>,
  },
];

const PANELS: Panel[] = [
  { id: "buy", label: "Buy", title: "Own gold by the gram", steps: BUY_STEPS },
  { id: "sell", label: "Sell", title: "Cash out in seconds", steps: SELL_STEPS },
  {
    id: "convert",
    label: "Deliver",
    title: "Convert into coins or bars",
    steps: DELIVERY_STEPS,
  },
];

// Sticky offset: clears the 72px nav, plus a per panel peek so the deck shows.
const STACK_TOP = 88;
const STACK_PEEK = 24;

// ── Desktop detection (stacking is enabled only where panels fit the screen) ──

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isDesktop;
}

// ── Step card (one of three inside a panel) ───────────────────────────────────

function StepCard({
  step,
  index,
  wide,
}: {
  step: Step;
  index: number;
  wide?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center lg:items-stretch",
        // The third card spans both mobile grid columns so it reads full width,
        // side by side (image left, caption right) instead of stacked.
        wide &&
          "col-span-2 flex-row items-stretch gap-4 sm:col-span-1 sm:flex-col sm:items-center sm:gap-0 lg:items-stretch"
      )}
    >
      {/* App-flow visual — every step uses the same desktop width. The shorter
          desktop height keeps the delivery panel aligned when a source image is taller. */}
      <div
        className={cn(
          "flex w-full justify-center rounded-card-sm bg-[#f1f4f9] px-4 pt-4",
          wide && "w-1/2 shrink-0 items-start pb-4 sm:w-full sm:items-stretch sm:pb-0"
        )}
      >
        <div
          className={cn(
            "relative w-full max-w-[260px] overflow-hidden rounded-card-sm border border-[#e6edf7] aspect-[735/1004] lg:aspect-auto lg:h-[clamp(340px,46vh,460px)] lg:w-[260px] lg:max-w-[260px]",
            // Full app screenshot stays visible in the narrow side-by-side
            // layout instead of being cropped by a cover fit.
            wide && "sm:aspect-[735/1004]"
          )}
        >
          <Image
            src={step.image}
            alt={step.alt}
            fill
            sizes="(max-width: 640px) 40vw, 340px"
            className={cn(
              "object-top",
              wide ? "object-contain" : "object-cover"
            )}
            priority={index === 0}
          />
        </div>
      </div>

      {/* Caption — flush with the image's left and right edges */}
      <div
        className={cn(
          "mt-4 w-full",
          wide && "mt-0 flex flex-col justify-center sm:mt-4"
        )}
      >
        <h4 className="text-sm font-bold leading-snug tracking-[-0.01em] text-primary-900 sm:text-base">
          {step.title}
        </h4>
        <p className="mt-1 text-xs leading-[1.45] text-text-body sm:text-sm">
          {step.body}
        </p>
      </div>
    </div>
  );
}

// ── Stacking panel ────────────────────────────────────────────────────────────

function StackPanel({
  panel,
  index,
  total,
  progress,
  stacking,
}: {
  panel: Panel;
  index: number;
  total: number;
  progress: MotionValue<number>;
  stacking: boolean;
}) {
  // Earlier panels shrink a touch, so the covered deck reads with depth.
  const targetScale = 1 - (total - 1 - index) * 0.03;
  const scale = useTransform(progress, [index / total, 1], [1, targetScale]);

  const top = STACK_TOP + index * STACK_PEEK;

  return (
    <motion.section
      aria-label={panel.title}
      style={
        stacking ? { top, scale, transformOrigin: "center top" } : undefined
      }
      className={cn(
        "rounded-card border border-[#e3ecfb] bg-white px-6 py-6 sm:px-10 sm:py-8 lg:px-12 lg:py-7",
        // Upward depth shadow so a covering panel separates from the one behind.
        "shadow-[0_-18px_50px_-22px_rgba(10,45,110,0.28),0_6px_20px_-14px_rgba(10,45,110,0.10)]",
        stacking ? "sticky" : "static",
        index < total - 1 && "mb-6 lg:mb-8"
      )}
    >
      <div className="flex items-center gap-3">
        <span className="font-heading text-sm font-bold tracking-[0.08em] text-accent-500 tabular-nums">
          0{index + 1}
        </span>
        <span className="h-4 w-px bg-primary-200" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-500">
          {panel.label}
        </span>
      </div>

      <h3 className="mt-3 text-[clamp(1.5rem,2.6vw,2.1rem)] font-semibold leading-[1.1] tracking-[-0.015em] text-primary-900">
        {panel.title}
      </h3>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:gap-7 md:grid-cols-3 lg:mt-7">
        {panel.steps.map((step, i) => (
          <StepCard
            key={`${panel.id}-${i}`}
            step={step}
            index={i}
            wide={i === 2}
          />
        ))}
      </div>
    </motion.section>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

const header: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

export function GoldActions() {
  const reduce = useReducedMotion() ?? false;
  const isDesktop = useIsDesktop();
  const stacking = isDesktop && !reduce;

  const deckRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: deckRef,
    offset: ["start start", "end end"],
  });

  return (
    <section
      className="relative bg-neutral-0"
      aria-labelledby="gold-actions-heading"
    >
      <div className="mx-auto max-w-[1440px] px-6 pb-16 pt-16 sm:px-10 sm:pb-20 sm:pt-20 lg:px-20 lg:pb-24 lg:pt-24">
        {/* Heading */}
        <motion.div
          variants={header}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="mb-10 max-w-[720px] lg:mb-14"
        >
          <motion.p
            variants={fadeUp(reduce)}
            className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500"
          >
            How it works
          </motion.p>
          <motion.h2
            id="gold-actions-heading"
            variants={fadeUp(reduce)}
            className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900 xl:whitespace-nowrap"
          >
            Three steps, whichever direction you&apos;re going
          </motion.h2>
          <motion.p
            variants={fadeUp(reduce)}
            className="mt-4 max-w-[560px] text-lg leading-[1.5] text-text-body"
          >
            Buying, selling and taking delivery all work the same way.
            Nothing hidden, nothing to learn.
          </motion.p>
        </motion.div>

        {/* Stacking deck of panels */}
        <div ref={deckRef} className="relative">
          {PANELS.map((panel, i) => (
            <StackPanel
              key={panel.id}
              panel={panel}
              index={i}
              total={PANELS.length}
              progress={scrollYProgress}
              stacking={stacking}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
